import { useConfirmModal } from '@/components/ConfirmModalProvider';
import { useModal } from '@/components/ModalProvider';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import config from '@/config';
import {
  ApiKey,
  AspNetRoles,
  AuditLogByEntityQuery,
  AuditLogInsertInput,
  CustomerByPkQuery,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { FeatureName } from '@/models/role.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import hasPermission from '@/utils/permission.function';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { Copy, Plus, X } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'sonner';
import { deleteApiKeyMutation } from '../../Devices/graphql/mutation';
import { createCustomerApiKeysMutation } from '../graphql/mutation';
import { allRolesQuery } from '../graphql/query';

interface CustomerApiKeysSectionProps {
  customerId: string;
  apiKeys: ApiKey[];
  refetch: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<CustomerByPkQuery, Error>>;
  refetchLog: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<AuditLogByEntityQuery, Error>>;
}

export function CustomerApiKeysSection({
  customerId,
  apiKeys,
  refetch,
  refetchLog,
}: CustomerApiKeysSectionProps) {
  const { openModal } = useConfirmModal();
  const modal = useModal();
  const apiKeyDescriptionRef = useRef<HTMLInputElement>(null);
  const selectedRoleIdRef = useRef<string | undefined>(undefined);
  const apiKeyDescriptionEnabled = config.apiKey.hasDescription;

  const { data: roleData } = useQuery({
    queryKey: ['allRoles'],
    queryFn: () =>
      execute({
        query: allRolesQuery,
      }),
    select: (data) => (data.aspNetRoles ?? []) as AspNetRoles[],
  });

  const { mutateAsync: createCustomerApiKeys, isPending } = useMutation({
    mutationKey: ['CreateCustomerApiKeysMutation'],
    mutationFn: (options: {
      input: {
        createCustomerApiKeys: {
          customerId: string;
          description?: string;
          roleId?: string;
        }[];
      };
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createCustomerApiKeysMutation,
        variables: {
          ...options,
        },
      });
    },
    onSuccess: async () => {
      await refetch();
      await refetchLog();
      toast.success('API key created successfully!', {
        duration: 3000,
      });
    },
  });

  const { mutateAsync: deleteApiKey } = useMutation({
    mutationKey: ['DeleteApiKey'],
    mutationFn: ({
      id,
      auditLogInput,
    }: {
      id: string;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: deleteApiKeyMutation,
        variables: {
          id,
          auditLogInput,
        },
      });
    },
    onSuccess: async () => {
      await refetch();
      await refetchLog();
      toast.success('API key removed successfully!', {
        duration: 3000,
      });
    },
  });

  const confirmDeleteApiKey = (id: string) => {
    const apiKey = apiKeys.find((t) => t.id === id);
    const displayKey = apiKey?.reference
      ? `${apiKey.reference}...`
      : apiKey?.key;
    openModal(
      {
        message: (
          <div className="grid gap-2">
            <p>Are you sure you want to delete this item?</p>
            {displayKey && (
              <div className="border-border bg-muted/30 rounded-md border px-3 py-2 text-sm">
                {apiKeyDescriptionEnabled && apiKey?.description && (
                  <div className="text-muted-foreground">
                    {apiKey.description}
                  </div>
                )}
                <div className="font-mono break-all">{displayKey}</div>
              </div>
            )}
          </div>
        ),
      },
      async () => {
        await deleteApiKey({
          id,
          auditLogInput: createAuditLogInput({
            action: AuditLogAction.DELETE_CUSTOMER_API_KEY,
            entities: [
              {
                entity: AuditLogEntityType.CUSTOMER,
                id: customerId,
              },
              {
                entity: AuditLogEntityType.API_KEY,
                id,
              },
            ],
          }),
        });
      },
    );
  };

  const addApiKey = async (description?: string, roleId?: string) => {
    const trimmedDescription = apiKeyDescriptionEnabled
      ? description?.trim()
      : undefined;

    const result = await createCustomerApiKeys({
      input: {
        createCustomerApiKeys: [
          {
            customerId,
            description: trimmedDescription || undefined,
            roleId: roleId || undefined,
          },
        ],
      },
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.CREATE_CUSTOMER_API_KEY,
        entities: [
          {
            id: customerId,
            entity: AuditLogEntityType.CUSTOMER,
          },
        ],
      }),
    });

    type CreateCustomerApiKeysResult = {
      createCustomerApiKeys?: {
        apiKeys?: { key: string }[];
      };
    };

    const typedResult = result as CreateCustomerApiKeysResult;
    const apiKey = typedResult.createCustomerApiKeys?.apiKeys?.[0]?.key;
    if (!apiKey) {
      toast.error('Failed to retrieve the new API key');
      return;
    }

    openApiKeyModal(apiKey);
  };

  const handleCopyApiKey = async (apiKey: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success('API key copied to clipboard');
    } catch (error) {
      console.error('Failed to copy API key', error);
      toast.error('Failed to copy API key');
    }
  };

  const openApiKeyModal = (apiKey: string) => {
    modal.openModal(
      <>
        <DialogHeader>
          <DialogTitle>API Key</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <div className="mx-1 mb-6 flex items-center justify-center gap-2 font-mono font-semibold">
            <span className="break-all">{apiKey}</span>
            <Button
              className="ms-4"
              type="button"
              variant={'outline'}
              size={'icon'}
              onClick={() => {
                void handleCopyApiKey(apiKey);
              }}
              aria-label="Copy API key"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div>
            <p className="text-red font-semibold">
              This API key is only displayed once.
            </p>
            <p className="text-muted-foreground text-sm">
              Make sure you copy it now.
            </p>
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant={'outline'} onClick={modal.closeModal}>
            Close
          </Button>
        </DialogFooter>
      </>,
    );
  };

  const openAddApiKeyModal = () => {
    selectedRoleIdRef.current = undefined;
    const roles: AspNetRoles[] = roleData ?? [];
    openModal(
      {
        title: 'Create API key',
        message: (
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="apiKeyRole">
                Role
                <span className="text-muted-foreground"> (optional)</span>
              </Label>
              <Select
                onValueChange={(v) => {
                  selectedRoleIdRef.current = v;
                }}
              >
                <SelectTrigger className="w-full" id="apiKeyRole" autoFocus>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.normalizedName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="apiKeyDescription">
                Description
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              {apiKeyDescriptionEnabled ? (
                <Input
                  id="apiKeyDescription"
                  ref={apiKeyDescriptionRef}
                  placeholder="e.g. Integration service"
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  Description is disabled for this environment.
                </p>
              )}
            </div>
          </div>
        ),
        confirmText: 'Create',
        cancelText: 'Cancel',
      },
      async () => {
        await addApiKey(
          apiKeyDescriptionRef.current?.value,
          selectedRoleIdRef.current,
        );
      },
    );
  };

  return (
    <Card className="relative">
      <CardHeader>
        <CardTitle>API Keys</CardTitle>
        {hasPermission(FeatureName.CREATE_UPDATE_API_KEYS) && (
          <Button
            variant={'outline'}
            size={'sm'}
            className="absolute top-4 right-4"
            onClick={openAddApiKeyModal}
            disabled={isPending}
          >
            <Plus /> Add key
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          {apiKeys.length > 0 ? (
            apiKeys.map((t) => (
              <div className="grid gap-1" key={t.id}>
                {apiKeyDescriptionEnabled && t.description && (
                  <div>
                    <span className="text-muted-foreground text-sm">
                      {t.description}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <div className="w-24 shrink-0">
                    <Input
                      type="text"
                      readOnly
                      id={`apiKeyId-${t.id}`}
                      placeholder="ID"
                      defaultValue={t.id as string}
                      title={t.id as string}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="text"
                      readOnly
                      id={`apiKey-${t.id}`}
                      placeholder="API key"
                      defaultValue={t.reference ? t.reference + '...' : t.key}
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      readOnly
                      id={`role-${t.id}`}
                      placeholder="No Role"
                      defaultValue={
                        t.roleId
                          ? (roleData?.find((role) => role.id === t.roleId)
                              ?.normalizedName ?? '')
                          : ''
                      }
                    />
                  </div>
                  {hasPermission(FeatureName.CREATE_UPDATE_API_KEYS) && (
                    <Button
                      variant={'outline'}
                      size={'icon'}
                      onClick={() => confirmDeleteApiKey(t.id as string)}
                    >
                      <X />
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-muted-foreground text-sm font-medium">
              No API keys
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
